/** @jsx jsx */

import { Fragment } from 'react';

import { css, jsx } from '@emotion/core';
import {
  Alert,
  Checkbox,
  CheckboxGroup,
  Field,
  RadioGroup,
  Radio,
  Spacer,
  Text,
  TextInput,
} from '@zapier/design-system';

import CtaCreateZap from 'app/developer-v3/components/CtaCreateZap';
import EditorBodyLayout from 'app/developer-v3/layouts/EditorBodyLayout';
import Link from 'app/common/components/Link';
import NotFound from 'app/developer-v3/components/NotFound';
import {
  useCurrentVersionReadOnlyReason,
  useForm,
  useRouter,
} from 'app/developer-v3/hooks';
import {
  ACTION_TYPES,
  BASE_DOC_URL_VB,
  NEW_SERVICE_FIELD,
} from 'app/developer-v3/constants';
import { getActionUrl, getAppVersionUrl } from 'app/developer-v3/utils';

import SearchOrCreate from './SearchOrCreate';
import { MIN_DESCRIPTION_CHARS, FIELD_VERTICAL_SPACING } from './constants';

import type { CreateActionSchema } from 'app/developer-v3/types';

const Styles = {
  booleanInput: css`
    display: flex;
    gap: 28px;
  `,
  ctaWrapper: css`
    margin-bottom: 30px;
  `,
  searchOrCreateWrapper: css`
    padding-left: 34px;
  `,
};

type Display = {
  description: string;
  hidden: boolean;
  label: string; // This is aliased as "name" in the UI.
};

type Settings = Display & {
  isEnabledSearchOrCreate?: boolean;
  key: string;
  noun: string;
  searchOrCreateCreateKey?: string;
  searchOrCreateLabel?: string;
  type: ACTION_TYPES;
};

type Props = {
  creates: Record<string, CreateActionSchema>;
  hasPerform: boolean;
  initialSettings: Settings;
  isActionFound: boolean;
  isActionNew: boolean;
  isSaving: any;
  onSubmit: (settingsNext: Settings) => Promise<void>;
  params: { id: string; actionKey: string; version: string };
};

function ActionSettings({
  creates,
  hasPerform,
  initialSettings,
  isActionFound,
  isActionNew,
  isSaving,
  onSubmit,
  params,
}: Props) {
  const form = useForm({
    enableReinitialize: true,
    initialValues: initialSettings,
    onSubmit,
    validate: valuesNext => {
      const {
        description,
        key,
        isEnabledSearchOrCreate,
        label,
        noun,
        searchOrCreateCreateKey,
        searchOrCreateLabel,
      } = valuesNext;
      const errorsNext: Partial<Record<keyof Settings, string>> = {};

      if (!description) {
        errorsNext.description = 'Description is required.';
      }
      if (description.length < MIN_DESCRIPTION_CHARS) {
        errorsNext.description = `Description must be at least ${MIN_DESCRIPTION_CHARS} characters.`;
      }
      if (!key) {
        errorsNext.key = 'Key is required.';
      }
      if (!/^[A-Z][A-Z0-9_]*$/i.test(key)) {
        errorsNext.key =
          'Keys must start with a letter and contain only alphanumeric and underscore characters.';
      }
      if (!label) {
        errorsNext.label = 'Label is required.';
      }
      if (!noun) {
        errorsNext.noun = 'Noun is required.';
      }
      if (isEnabledSearchOrCreate) {
        if (Object.values(creates).length === 0) {
          errorsNext.searchOrCreateCreateKey =
            'You need to define a Create Action.';
        } else if (!searchOrCreateCreateKey) {
          errorsNext.searchOrCreateCreateKey = 'Create action is required.';
        }

        if (!searchOrCreateLabel) {
          errorsNext.searchOrCreateLabel = 'Find or Create label is required.';
        }
      }

      return errorsNext;
    },
  });

  const isFormReadOnly = !!useCurrentVersionReadOnlyReason() || isSaving;
  const { routeTo } = useRouter();
  const baseRoute = getAppVersionUrl(params.id, params.version);
  const cancelRoute = `${baseRoute}/actions`;

  return !isActionNew && !isActionFound ? (
    <NotFound
      escapeRoute={cancelRoute}
      noun="action"
      unknownKey={params.actionKey}
    />
  ) : (
    <EditorBodyLayout
      cancelRoute={cancelRoute}
      isDisabled={
        isFormReadOnly || !form.isDirty || !form.isValid || form.isSubmitting
      }
      isSaving={isSaving}
      onSave={form.onSubmit}
      saveText={
        params.actionKey === NEW_SERVICE_FIELD ? 'Save and continue' : 'Save'
      }
    >
      <aside css={Styles.ctaWrapper}>
        {hasPerform && <CtaCreateZap type="action" />}
        <Alert status="info">
          <Link
            href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements#5-7-integration-uses-english-language`}
            target="_blank"
          >
            Use English
          </Link>{' '}
          for all user-facing text.{' '}
          <Link
            href={`${BASE_DOC_URL_VB}/publish/integration-build-guidelines#action-design-and-copy`}
            target="_blank"
          >
            Learn more
          </Link>{' '}
          about action design and copy.
        </Alert>
      </aside>
      <RadioGroup
        errorMessage={
          form.isTouched.type ? form.errors.type || undefined : undefined
        }
        isDisabled={isFormReadOnly || !isActionNew}
        isInvalid={Boolean(form.isTouched.type && form.errors.type)}
        isRequired={true}
        label="Type"
        onChange={value => {
          const typeNext = value as ACTION_TYPES;
          if (!form.isTouched.type) {
            form.setFieldTouched('type', true);
          }
          form.setFieldValue('type', typeNext);
          if (form.values.type !== typeNext) {
            const newRoute = `${getActionUrl({
              actionKey: NEW_SERVICE_FIELD,
              actionType: typeNext,
              id: params.id,
              version: params.version,
            })}/settings`;
            routeTo(newRoute);
          }
        }}
        value={form.values.type}
      >
        <div css={Styles.booleanInput}>
          <Radio value={ACTION_TYPES.creates}>Create</Radio>
          <Radio value={ACTION_TYPES.searches}>Search</Radio>
        </div>
      </RadioGroup>
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        error={form.isTouched.key ? form.errors.key : ''}
        helpTextLineClamp={2}
        // Key is not editable if it has been created.
        isDisabled={isFormReadOnly || !isActionNew}
        isRequired={true}
        label="Key"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Enter a unique identifier for this action using snake_case
            (lowercase with underscores). Example:{' '}
            <code>
              {form.values.type === ACTION_TYPES.creates ? 'create' : 'find'}
              _ticket
            </code>
            .
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="key"
            name="key"
            onBlur={form.onBlur}
            onChange={form.onChange}
            placeholder={`${
              form.values.type === ACTION_TYPES.creates ? 'create' : 'find'
            }_ticket`}
            value={form.values.key}
          />
        )}
      />
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        error={form.isTouched.label ? form.errors.label : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        isRequired={true}
        label="Label"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Enter a user-friendly name for this action to describe what it does
            (creates, updates, or searches) and which resource it operates on.
            Capitalize each word. Displayed to users in the Zapier UI. Example:{' '}
            <code>
              {form.values.type === ACTION_TYPES.creates ? 'Create' : 'Find'}{' '}
              Ticket
            </code>
            .
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="label"
            name="label"
            onBlur={form.onBlur}
            onChange={form.onChange}
            placeholder={`${
              form.values.type === ACTION_TYPES.creates ? 'Create' : 'Find'
            } Ticket`}
            value={form.values.label}
          />
        )}
      />
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        error={form.isTouched.noun ? form.errors.noun : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        isRequired={true}
        label="Noun"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Specify the main resource this action operates on. This should match
            the main resource entered in the Name field above. Capitalize all
            words. Displayed to users in the Zapier UI. Example:{' '}
            <code>Ticket</code>.
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="noun"
            name="noun"
            onBlur={form.onBlur}
            onChange={form.onChange}
            placeholder="Ticket"
            value={form.values.noun}
          />
        )}
      />
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        error={form.isTouched.description ? form.errors.description : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        isRequired={true}
        label="Description"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Clearly describe what this action does in a complete sentence.
            Displayed to users in the Zapier UI. Example:{' '}
            <code>
              {form.values.type === ACTION_TYPES.creates ? 'Creates' : 'Finds'}{' '}
              a ticket in the system.
            </code>{' '}
            .
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="description"
            name="description"
            onBlur={form.onBlur}
            onChange={form.onChange}
            placeholder={`${
              form.values.type === ACTION_TYPES.creates ? 'Creates' : 'Finds'
            } a ticket in the system.`}
            value={form.values.description}
          />
        )}
      />
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <RadioGroup
        errorMessage={
          form.isTouched.hidden ? form.errors.hidden || undefined : undefined
        }
        helpText={
          <Text>
            Show or hide this action in the Editor when your users are setting
            up a Zap with your integration.{' '}
            <Link
              href="https://platform.zapier.com/build/action"
              target="_blank"
            >
              Learn more about visibility options.
            </Link>
          </Text>
        }
        isDisabled={isFormReadOnly}
        isInvalid={Boolean(form.isTouched.hidden && form.errors.hidden)}
        isRequired={true}
        label="Visibility in Editor"
        onChange={value => {
          form.setFieldValue('hidden', value === 'hidden');
        }}
        value={form.values.hidden ? 'hidden' : 'shown'}
      >
        <div css={Styles.booleanInput}>
          <Radio value="shown">Shown</Radio>
          <Radio value="hidden">Hidden</Radio>
        </div>
      </RadioGroup>
      {form.values.type === ACTION_TYPES.searches && (
        <Fragment>
          <Spacer height={FIELD_VERTICAL_SPACING} />
          <CheckboxGroup
            isDisabled={isFormReadOnly}
            label="Enable Create if not found"
            onChange={keys => {
              const isSelected = keys.includes('search-or-create');
              form.setFieldTouched('searchOrCreateCreateKey', isSelected);
              form.setFieldValue('isEnabledSearchOrCreate', isSelected);
            }}
            value={
              form.values.isEnabledSearchOrCreate ? ['search-or-create'] : []
            }
          >
            <Checkbox value="search-or-create">
              Pair an existing Search action and a Create action to enable
              &quot;Find or Create&quot; functionality?
            </Checkbox>
          </CheckboxGroup>
        </Fragment>
      )}
      {form.values.type === ACTION_TYPES.searches &&
        form.values.isEnabledSearchOrCreate && (
          <fieldset css={Styles.searchOrCreateWrapper}>
            <SearchOrCreate
              creates={creates}
              errorSearchOrCreateCreateKey={
                form.isTouched.searchOrCreateCreateKey
                  ? form.errors.searchOrCreateCreateKey
                  : ''
              }
              errorSearchOrCreateLabel={
                form.isTouched.searchOrCreateLabel
                  ? form.errors.searchOrCreateLabel
                  : ''
              }
              isDisabled={isFormReadOnly}
              onBlurSearchOrCreateCreateKey={form.onBlur}
              onBlurSearchOrCreateLabel={form.onBlur}
              onChangeCreateKey={createKeyNext =>
                form.setFieldValue('searchOrCreateCreateKey', createKeyNext)
              }
              onChangeSearchOrCreateLabel={searchOrCreateLabelNext =>
                form.setFieldValue(
                  'searchOrCreateLabel',
                  searchOrCreateLabelNext
                )
              }
              searchOrCreateCreateKey={form.values.searchOrCreateCreateKey}
              searchOrCreateLabel={form.values.searchOrCreateLabel}
            />
          </fieldset>
        )}
    </EditorBodyLayout>
  );
}

export default ActionSettings;
